/* eslint-disable react/prop-types */
import React from 'react';
import get from 'lodash.get';
import config from 'Config/config.json';

import { getCustomizedAdConfig } from 'SharedHelpers/getCustomizedAdConfig';

import { Ad } from 'Components/atoms/Ad';
import { getNativeAd } from 'SharedPartials/getNativeAd';
import { getLoadMore } from 'SharedPartials/getLoadMore';
import { getSplitIndexContent } from 'SharedPartials/getSplitIndexContent';

/**
 * @name getIndexArticlePageContent
 * @param index
 * @param props
 * @param adCountStart
 * @returns {JSX.Element|boolean}
 */
export const getIndexArticlePageContent = (
  index,
  props,
  adCountStart = {
    countInlineAdsDesktop: 0,
    countInlineAdsMobile: 0,
  },
  isMobile = false,
) => {
  if (!index) {
    return false;
  }

  const {
    articleCardProps = {},
    articlesPerRow = 3,
    extraClassNames = {
      'polaris__review--consider': true,
      'polaris__content--index-related': true,
      'polaris__core-content': true,
      'polaris__core-content-index': true,
      'polaris__core-content-index-article': true,
    },
    pagination: {
      hasMore,
      isLoading,
      loadMore,
      nextUrl,
      pages,
      setPageRef,
    },
  } = props;

  const defaultArticleProps = {
    datePosition: 'meta',
    headingSize: 4,
  };

  let inlineAdPositionDesktop = adCountStart?.countInlineAdsDesktop;
  let inlineAdPositionMobile = adCountStart?.countInlineAdsMobile;

  // count ads per page
  let adCounterMobile = 0;
  let adCounterDesktop = 0;

  const renderItems = (related, isFirst = false) => {
    // total number of items
    const totalContent = related.length;

    const contentWithAds = related.map(item => ({ ...item }));

    if (isFirst) {
      // Check if we have enough results
      if (totalContent >= 3) {
        // Add native ad at 4th position
        contentWithAds.splice(3, 0, {
          component: [
            <React.Fragment key="native_ad">
              {getNativeAd({
                id: 'native_article',
                targeting: {
                  position: 'native_article',
                  placement: 'native_article',
                  refresh: 'no',
                },
              })}
            </React.Fragment>,
          ],
        });
      }
    }

    const desktopAdPoints = get(config, 'adBreakPoints.default.desktop.default');
    const mobileAdPoints = get(config, 'adBreakPoints.default.mobile.default');

    const adverts = mobileAdPoints
      .filter(item => item <= totalContent)
      .map((e, j) => {
        adCounterMobile += 1;

        // increase the count
        // if it hits the length of the array, reset count
        inlineAdPositionMobile += 1;
        if (inlineAdPositionMobile > 10) inlineAdPositionMobile = 1;

        return ({
          name: 'Mobile Ad',
          placement: e,
          position: inlineAdPositionMobile,
          adCounter: adCounterMobile,
          index: j,
          component: (
            <Ad
              extraClassNames={{
                'hide-desktop': true,
              }}
              id={`refresh-inline_${inlineAdPositionMobile}`}
              isFullWidth
              isSkippable
              isSpaceReserved
              key={`mobile-inline-index-article-${inlineAdPositionMobile}`}
              targetViewport="mobile"
              targeting={{
                position: `inline_${inlineAdPositionMobile}`,
                placement: `inline_${inlineAdPositionMobile}`,
                refresh: 'yes',
              }}
              {...getCustomizedAdConfig({
                desktopRule: false,
                mobileRule: true,
              })}
            />
          ),
        });
      })
      .concat(
        desktopAdPoints
          .filter(item => item <= totalContent)
          .map((e, j) => {
            adCounterDesktop += 1;

            // increase the count
            // if it hits the length of the array, reset count
            inlineAdPositionDesktop += 1;
            if (inlineAdPositionDesktop > 10) inlineAdPositionDesktop = 1;

            return ({
              name: 'Desktop Ad',
              placement: e,
              position: inlineAdPositionDesktop,
              adCounter: adCounterDesktop,
              index: j,
              component: (
                <Ad
                  extraClassNames={{
                    'hide-mobile': true,
                    'hide-tablet': true,
                  }}
                  id={`refresh-inline_${inlineAdPositionDesktop}`}
                  isFullWidth
                  isSkippable
                  isSpaceReserved
                  key={`desktop-inline-index-article-${inlineAdPositionDesktop}`}
                  targetViewport="desktop"
                  targeting={{
                    position: `inline_${inlineAdPositionDesktop}`,
                    placement: `inline_${inlineAdPositionDesktop}`,
                    refresh: 'yes',
                  }}
                  {...getCustomizedAdConfig({
                    desktopRule: true,
                    mobileRule: false,
                  })}
                />
              ),
            });
          }),
      );

    adverts.sort((a, b) => (a.placement > b.placement ? 1 : -1))
      .map((e, j) => contentWithAds.splice(e.placement + j, 0, e));

    // Do the same thing here
    const articleGroupedContent = getSplitIndexContent({
      content: contentWithAds,
      title: '',
      extraClassNames,
      articlesPerRow,
      articleCardProps: { ...defaultArticleProps, ...articleCardProps },
      isMobile,
    });

    return (
      articleGroupedContent
    );
  };

  const loadMoreParams = {
    label: isLoading ? 'Loading...' : 'Load More',
    loadMore,
    isLoading,
    extraClassNames: {
      'polaris__load-more--small': true,
    },
    href: nextUrl,
  };

  return (
    <>
      <div data-previous="1" ref={setPageRef} />
      {Object.entries(pages).map(([page, items]) => (
        <div data-page={page} key={page} ref={setPageRef}>
          {renderItems(items, page === '1')}
        </div>
      ))}
      <div className="polaris__core-content">
        {hasMore && getLoadMore(loadMoreParams)}
      </div>
    </>
  );
};
